import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import type { Attempt } from '../components/AttemptLog';

interface AttemptsResponse {
  data: Attempt[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
}

async function fetchAttempts(procurementItemId: string): Promise<AttemptsResponse> {
  return apiFetch<AttemptsResponse>(`/api/v1/procurement/${procurementItemId}/attempts?limit=50`);
}

async function createAttempt(procurementItemId: string, attemptType: string, note: string) {
  return apiFetch<{ data: Attempt }>(`/api/v1/procurement/${procurementItemId}/attempts`, {
    method: 'POST',
    body: JSON.stringify({ attemptType, note }),
  });
}

export function useProcurementAttempts(procurementItemId: string | null) {
  return useQuery({
    queryKey: ['procurement', 'attempts', procurementItemId],
    queryFn: () => fetchAttempts(procurementItemId!),
    enabled: !!procurementItemId,
    staleTime: 30000,
  });
}

export function useCreateAttempt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      procurementItemId,
      attemptType,
      note,
    }: {
      procurementItemId: string;
      attemptType: string;
      note: string;
    }) => createAttempt(procurementItemId, attemptType, note),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['procurement', 'attempts', variables.procurementItemId],
      });
      queryClient.invalidateQueries({ queryKey: ['procurement', 'items'] });
    },
  });
}
